import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DailyPostDoc, MediaRef, UserDoc } from '../../../shared/types';
import { DAILY_NAME, previousDay, streakDayFor, streakOf, shownMedia } from '../../../shared/constants';
import { avatarOf, markDailySeen, setDailyReaction, urlFor, type DayPost, type WithId } from '../data';
import { atRisk, firstIn, isFamily, standing, type Member } from '../family';
import { isGiviSnap, isUnseenBy, splitFeed } from '../feed';
import { unreadOf, useSeenTalk, waitingOnMe } from '../seenTalk';
import type { TodayState } from '../hooks';
import { go, useBackCloses } from '../router';
import { RecapCard } from './Recap';
import type { RecapDoc } from '../../../shared/recap';
import {
  Avatar,
  Chip,
  EmptyState,
  IconCamera,
  IconChat,
  IconCheck,
  IconPlus,
  MediaWell,
  ReactionBar,
  Spinner,
  emojisOf,
  tallyEmoji,
  useHold,
} from '../ui';
import { Photo } from '../Photo';

/**
 * How late, in words.
 *
 * Minutes up to an hour, hours after that. "94 min late" is arithmetic the
 * reader has to do; an hour and a half is the thing they wanted to know.
 */
export function lateText(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

interface Props {
  uid: string;
  users: Record<string, UserDoc>;
  today: TodayState;
  onOpenCamera: () => void;
  /** Not posted for the prompt in hand: everyone else's are frosted over. */
  locked?: boolean;
  /** Straight into taking a GiviSnap, which is the way in when locked. */
  onTakeGiviSnap?: () => void;
  /** The Sunday recap, while one is out. */
  recap?: RecapDoc | null;
}

/** A flame, drawn rather than typed: emoji is not an icon. */
function Flame({ size = 12, color }: { size?: number; color: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3c.6 3.5 3.2 4.6 3.2 7.6a3.2 3.2 0 0 1-6.4 0c0-1 .4-1.8.9-2.5C8 9.6 6 11 6 14a6 6 0 0 0 12 0c0-4.5-3.6-7.7-6-11z" />
    </svg>
  );
}

/** Nought reads as no streak at all rather than as a score of zero. */
function StreakMark({ days, quiet }: { days: number; quiet?: boolean }) {
  if (days < 1) return null;
  const colour = quiet ? 'var(--muted)' : 'var(--accent)';
  return (
    <span className="streak-mark" aria-label={`${days} day streak`}>
      <Flame color={colour} size={quiet ? 10 : 11} />
      <span style={{ color: colour }}>{days}</span>
    </span>
  );
}

export function Today({ uid, users, today, onOpenCamera, locked = false, onTakeGiviSnap = onOpenCamera, recap = null }: Props) {
  const lockedFor = (author: string) => locked && author !== uid;
  const day = today.day;
  // A card being held, and where it is on screen, for the reactions over it.
  const [reacting, setReacting] = useState<{ post: DayPost; at: DOMRect } | null>(null);
  /*
   * The day the streaks are counting, which trails the calendar until the
   * prompt fires: a run ends at the next reminder, not at midnight, so until
   * one goes out everybody is still being counted for yesterday.
   */
  const counting = streakDayFor(day, Boolean(today.daily?.firedAt));
  const before = previousDay(counting);

  /*
   * Two lists out of the same posts, because they answer two questions.
   *
   * `posts` is what there is to look at: both days the screen holds, so a
   * GiviSnap does not vanish the instant the next prompt fires. Filtering this
   * one to the day being counted is the mistake that hid Dad's — it was made
   * seventeen minutes before the prompt, so it answers the previous one, and
   * the tab it was posted to is exactly where people went looking for it.
   *
   * `answering` is who has done this one. That question has to stay on a
   * single day or it tells somebody they are finished when they are not.
   *
   * Extras are in neither. A story is posted for its own sake and lives on
   * the camera tab; letting it in here would make the rail, the streak and
   * the roster all say somebody was in when they were not.
   */
  const posts = today.posts.filter(isGiviSnap);
  const answering = posts.filter((post) => post.day === counting);

  /*
   * Whether each post was new to you when this visit first saw it, and it stays
   * that way until you leave the tab. A card is marked seen by being looked at,
   * so reading the split off seenBy afresh each render would drop a card below
   * the line the moment it had been read -- the feed moving under the thumb
   * reading it. Held in a ref and filled as posts arrive, so one landing while
   * the screen is open joins the new ones rather than the old; written during
   * render only for ids it has not met, which gives the same answer however
   * many times React renders. The next visit starts clean, since leaving the
   * tab unmounts this.
   */
  const newOnArrival = useRef(new Map<string, boolean>());
  for (const post of posts) {
    if (!newOnArrival.current.has(post.id)) {
      newOnArrival.current.set(post.id, isUnseenBy(post, uid));
    }
  }
  const freshIds = new Set(
    posts.filter((post) => newOnArrival.current.get(post.id)).map((post) => post.id),
  );

  /*
   * Newest at the top in each half. Sorted here rather than in watchToday:
   * that list is also what the pager swipes through, and there the order
   * people expect is the order things happened.
   */
  const feed = splitFeed(posts, freshIds);

  // Your own never waits to be opened, and neither does one you have seen.
  const waiting = new Map<string, number>();
  for (const post of posts) {
    if (!isUnseenBy(post, uid)) continue;
    waiting.set(post.data.authorUid, (waiting.get(post.data.authorUid) ?? 0) + 1);
  }

  // Both of these are about the prompt in hand, so both read the narrower list.
  const answered = (author: string) =>
    answering.some((post) => post.data.authorUid === author);
  const mine = answering.find((post) => post.data.authorUid === uid);

  const myStreak = streakOf(users[uid], counting, before, counting === day && Boolean(mine));

  // Everyone bar you, with whoever still owes you a look sorted to the front.
  const roster = Object.entries(users)
    .filter(([id]) => id !== uid)
    .map(([id, user]) => ({
      id,
      user,
      waiting: waiting.get(id) ?? 0,
      posted: answered(id),
      streak: streakOf(user, counting, before, counting === day && answered(id)),
    }))
    /*
     * Whoever has posted first, then the longest streak down, which is what
     * Carter asked the rail to show. Neither moves when a photo is read, so a
     * face no longer slides away while you look at it; the ring and its count
     * are what say there is something new.
     */
    .sort(
      (a, b) =>
        Number(b.posted) - Number(a.posted) ||
        b.streak - a.streak ||
        (a.user.displayName ?? '').localeCompare(b.user.displayName ?? ''),
    );

  /*
   * The family counted together, which leaves out the test account: it is in
   * the rail like anyone, but it never posts, and counted it would hold the
   * family streak at nought and share in nothing it had earned.
   */
  const family = Object.entries(users).filter(([, user]) => isFamily(user));
  const members: Member[] = family.map(([id, user]) => ({
    id,
    streak: streakOf(user, counting, before, counting === day && answered(id)),
    posted: answered(id),
  }));
  const together = standing(members);
  const familyIds = new Set(family.map(([id]) => id));
  const first = firstIn(answering, (id) => familyIds.has(id));
  const member = (id: string) => members.find((m) => m.id === id);

  // One at a time: the point of opening a daily out is that it gets the room.

  return (
    <>
      <div className="header" style={{ gap: 10 }}>
        <h1 className="title">{DAILY_NAME}s</h1>
        {myStreak > 0 && (
          <span className="streak-chip">
            <Flame color="var(--accent)" size={13} />
            <span>{myStreak}</span>
          </span>
        )}
        <span className="grow" />
      </div>

      <div className="scroll pad col" style={{ gap: 14, paddingBottom: 12 }}>
        {recap && <RecapCard recap={recap} />}

        {!today.daily?.firedAt && (
          <WindowCard today={today} hasPosted={Boolean(mine)} onOpenCamera={onOpenCamera} />
        )}

        {/*
          * Everyone, not only the people with something waiting: the streak is
          * the point of the rail, and it disappearing the moment you opened
          * their daily made the family look half empty.
          */}
        {together.size > 0 && (
          <FamilyBar
            posted={together.posted}
            size={together.size}
            streak={together.streak}
            today={counting === day}
          />
        )}

        <div className="roster-rail">
          <button className="roster-item" onClick={locked ? onTakeGiviSnap : onOpenCamera}>
            <span
              className={mine ? 'ring ring--mine' : 'ring ring--you'}
            >
              {mine ? (
                <Avatar
                  name={users[uid]?.displayName ?? 'You'}
                  photoURL={avatarOf(users[uid])}
                  size={54}
                  style={FACE}
                />
              ) : (
                <span className="roster-add">
                  <IconPlus size={20} />
                </span>
              )}
              <FaceMarks
                streak={myStreak}
                atRisk={Boolean(member(uid) && atRisk(member(uid)!))}
                crowned={together.crowned.has(uid)}
                first={first === uid}
              />
            </span>
            <span className="roster-name" style={{ color: 'var(--accent)' }}>
              {mine ? 'Add' : 'You'}
            </span>
          </button>

          {roster.map((person) => (
            <button
              key={person.id}
              className="roster-item"
              disabled={!person.posted}
              // The day being answered, which is what `posted` was decided on.
              // The calendar day would open a collection that, before the
              // prompt fires, holds nothing of theirs at all.
              onClick={() => {
                if (!person.posted) return;
                if (locked) onTakeGiviSnap();
                else go(`daily/${counting}/${person.id}`);
              }}
            >
              <span
                className={
                  person.waiting > 0
                    ? 'ring'
                    : person.posted
                      ? 'ring ring--seen'
                      : 'ring ring--empty'
                }
              >
                <Avatar
                  name={person.user.displayName ?? '?'}
                  photoURL={avatarOf(person.user)}
                  size={54}
                  style={FACE}
                />
                {person.waiting > 0 && (
                  <span className="roster-badge">{person.waiting}</span>
                )}
                <FaceMarks
                  streak={person.streak}
                  atRisk={Boolean(member(person.id) && atRisk(member(person.id)!))}
                  crowned={together.crowned.has(person.id)}
                  first={first === person.id}
                />
              </span>
              <span
                className="roster-name"
                style={{ color: person.posted ? 'var(--text-2)' : 'var(--dim)' }}
              >
                {person.user.displayName ?? 'Someone'}
              </span>
            </button>
          ))}
        </div>

        {feed.fresh.map((post) => (
          <RestingCard
            key={post.id}
            post={post}
            uid={uid}
            fresh
            author={users[post.data.authorUid]}
            authorStreak={streakOf(
              users[post.data.authorUid],
              counting,
              before,
              counting === day && answered(post.data.authorUid),
            )}
            locked={lockedFor(post.data.authorUid)}
            onHold={(at) => setReacting({ post, at })}
            onOpen={() =>
              lockedFor(post.data.authorUid) ? onTakeGiviSnap() : go(`daily/${post.day}/${post.id}`)
            }
          />
        ))}

        {/*
          * Between what is new and what is not, and there whenever there is
          * anything to see: under the last new one, or at the very top when
          * nothing is new, so a glance says there is nothing waiting.
          */}
        {/* Not while frosted: nothing above it has been looked at. */}
        {posts.length > 0 && !locked && (
          <div className="caught-up" role="separator">
            <IconCheck size={14} />
            <span>You're all caught up</span>
          </div>
        )}

        {feed.seen.map((post) => (
          <RestingCard
            key={post.id}
            post={post}
            uid={uid}
            author={users[post.data.authorUid]}
            authorStreak={streakOf(
              users[post.data.authorUid],
              counting,
              before,
              counting === day && answered(post.data.authorUid),
            )}
            /*
             * The photograph, named by its own id rather than by whoever took
             * it. Naming the person opened their earliest of the day, because
             * that is the first one a search by author finds -- so tapping the
             * newest GiviSnap in the feed showed the oldest.
             */
            locked={lockedFor(post.data.authorUid)}
            onHold={(at) => setReacting({ post, at })}
            onOpen={() =>
              lockedFor(post.data.authorUid) ? onTakeGiviSnap() : go(`daily/${post.day}/${post.id}`)
            }
          />
        ))}

        {posts.length === 0 && (
          <EmptyState
            icon={<IconCamera size={22} />}
            title="Nobody has posted yet"
            body={`When the ${DAILY_NAME} fires, everyone has two minutes. Whatever lands shows up here.`}
          />
        )}
      </div>
      {reacting && (
        <CardReactions
          post={reacting.post}
          uid={uid}
          at={reacting.at}
          onClose={() => setReacting(null)}
        />
      )}
    </>
  );
}

/**
 * The reactions, over a GiviSnap held in the feed.
 *
 * Carter: pressing and holding one should give the reactions, without opening
 * it. The same bar as in the viewer, laid across the card where the thumb is,
 * with everything else dimmed; a pick reacts and closes it, and so does a tap
 * anywhere off it or the phone's back. First in it, a way to comment, which
 * opens the GiviSnap on its comments.
 *
 * Unzoomed, like the chat's hold menu: the card's place is measured in screen
 * pixels, and the keys should not shrink with the text size.
 */
function CardReactions({
  post,
  uid,
  at,
  onClose,
}: {
  post: DayPost;
  uid: string;
  at: DOMRect;
  onClose: () => void;
}) {
  useBackCloses(onClose);
  const mine = emojisOf(post.data.reactions?.[uid]);
  const middle = at.top + at.height / 2 - CARD_BAR_PX / 2;
  const top = Math.min(Math.max(middle, CARD_BAR_EDGE_PX), window.innerHeight - CARD_BAR_PX - CARD_BAR_EDGE_PX);

  const pick = (emoji: string) => {
    const next = mine.includes(emoji) ? mine.filter((each) => each !== emoji) : [...mine, emoji];
    void setDailyReaction(post.day, post.id, uid, next).catch(() => undefined);
    onClose();
  };

  return createPortal(
    <div className="card-react">
      <span className="card-react-scrim" onClick={onClose} />
      <div className="card-react-bar" style={{ top }}>
        <ReactionBar
          mine={mine}
          onPick={pick}
          lead={
            <button
              type="button"
              className="reaction-key reaction-comment"
              aria-label="Comment"
              onClick={() => {
                go(`daily/${post.day}/${post.id}/comments`);
                onClose();
              }}
            >
              <IconChat size={22} />
            </button>
          }
        />
      </div>
    </div>,
    document.body,
  );
}

/** The bar's height, to centre it on the card, and how near an edge it may go. */
const CARD_BAR_PX = 64;
const CARD_BAR_EDGE_PX = 16;

/**
 * How the family is doing together, above the faces.
 *
 * A count and a bar of one segment each, so an empty slot is a person rather
 * than a percentage; and the family streak, which is the number worth keeping
 * up as one. Until the day's prompt fires the day being counted is still last
 * night's, and saying "today" then would be wrong about which one it means.
 */
function FamilyBar({
  posted,
  size,
  streak,
  today,
}: {
  posted: number;
  size: number;
  streak: number;
  today: boolean;
}) {
  const everyone = posted >= size;
  return (
    <div className={everyone ? 'family-bar is-everyone' : 'family-bar'}>
      <div className="family-top">
        <span className="family-count">
          {everyone ? (
            <b>Everyone's in {today ? 'today' : 'for yesterday'}</b>
          ) : (
            <>
              <b>{posted}</b> of {size} in {today ? 'today' : 'for yesterday'}
            </>
          )}
        </span>
        <span className={streak > 0 ? 'family-streak' : 'family-streak is-none'}>
          <Flame color="currentColor" size={13} />
          {streak > 0 ? `${streak} day family streak` : 'No family streak yet'}
        </span>
      </div>
      <div className="family-segments" aria-hidden="true">
        {Array.from({ length: size }, (_, i) => (
          <span key={i} className={i < posted ? 'is-in' : undefined} />
        ))}
      </div>
    </div>
  );
}

/** A crown, drawn: emoji is not an icon. */
function Crown() {
  return (
    <svg width="18" height="14" viewBox="0 0 24 18" aria-hidden="true">
      <path d="M2 4.5 7.2 9 12 1.5 16.8 9 22 4.5 19.8 16H4.2Z" fill="currentColor" />
    </svg>
  );
}

/**
 * What sits on a face besides the face.
 *
 * The streak moved here from under the name, where it was ten-pixel type and
 * easy to miss. Amber when it is on the line: a run holds until the next
 * prompt fires, so anyone with one who has not answered this prompt loses it
 * then. The crown is the longest run in the family and the 1st is whoever
 * answered this prompt first -- both counted among the family only, so the
 * test account never takes either.
 *
 * Four corners of one circle: the crown on top, the 1st at the upper left,
 * the waiting count (drawn by the caller) at the upper right, the streak along
 * the bottom.
 */
function FaceMarks({
  streak,
  atRisk,
  crowned,
  first,
}: {
  streak: number;
  atRisk: boolean;
  crowned: boolean;
  first: boolean;
}) {
  return (
    <>
      {crowned && (
        <span className="face-crown" role="img" aria-label="Longest streak in the family">
          <Crown />
        </span>
      )}
      {first && (
        <span className="face-first" aria-label="First in">
          1st
        </span>
      )}
      {streak > 0 && (
        <span
          className={atRisk ? 'face-streak is-at-risk' : 'face-streak'}
          aria-label={atRisk ? `${streak} day streak, on the line` : `${streak} day streak`}
        >
          <Flame color="currentColor" size={10} />
          {streak}
        </span>
      )}
    </>
  );
}

/**
 * A roster face at its full size whatever the text size is.
 *
 * Given as a style rather than through `size`, because Avatar works its own
 * width, height and font size out from that number — and the whole point here
 * is a length the zoom does not get to scale.
 *
 * The ring around it is already --face across, so the face fills whatever the
 * ring leaves inside its coloured edge. Sized to --face itself it came out that
 * edge's width too big, sat low and to the right, and left the ring showing as
 * an arc at its top-left corner.
 */
const FACE = {
  width: '100%',
  height: '100%',
  fontSize: 'calc(var(--face) * 0.38)',
  borderRadius: 'var(--r-pill)',
} as const;

/**
 * A GiviSnap at rest: the photograph, full width, with whose it is on it.
 *
 * The reaction and comment counts used to sit in a strip underneath. They are
 * gone: at this width the photograph fills the screen and a row of chips below
 * every one of them turned a feed into a list of cards. What is on the picture
 * stays on the picture.
 */
/**
 * How long a card has to be properly on screen before it counts as seen.
 *
 * Long enough that flicking past on the way down the feed does not count, short
 * enough that stopping to look does. Properly on screen means most of it: a
 * sliver of photograph at the bottom edge has not been looked at.
 */
export const SEEN_AFTER_MS = 1000;
const SEEN_WHEN_SHOWING = 0.6;

/**
 * Marks a post seen once it has sat on screen for a moment.
 *
 * Opening one full screen already did this, through the pager. Now that new
 * GiviSnaps are in the feed themselves, being looked at there has to count as
 * well, or the dot on the tab would only ever clear for somebody who tapped
 * every photograph open.
 *
 * Only while it is not yet seen. Once seenBy has you in it the observer goes,
 * so an old card scrolled past writes nothing.
 */
function useSeenWhenShown(post: DayPost, uid: string, visible = true) {
  const node = useRef<HTMLButtonElement>(null);
  // A frosted card has not been looked at, and must keep its dot on the tab.
  const unseen = visible && isUnseenBy(post, uid);

  useEffect(() => {
    const card = node.current;
    if (!unseen || !card || typeof IntersectionObserver === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer ??= setTimeout(() => {
            observer.disconnect();
            void markDailySeen(post.day, post.id, uid).catch(() => undefined);
          }, SEEN_AFTER_MS);
        } else if (timer !== undefined) {
          // Scrolled away before the moment was up: that was not a look.
          clearTimeout(timer);
          timer = undefined;
        }
      },
      { threshold: SEEN_WHEN_SHOWING },
    );
    observer.observe(card);
    return () => {
      observer.disconnect();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [unseen, post.day, post.id, uid]);

  return node;
}

function RestingCard({
  post,
  uid,
  fresh = false,
  author,
  authorStreak,
  locked = false,
  onHold,
  onOpen,
}: {
  post: DayPost;
  uid: string;
  /** Above the caught-up line for this visit, which is what the marker says. */
  fresh?: boolean;
  author?: UserDoc;
  authorStreak: number;
  /** Someone else's, before you have posted yours: who and when, not what. */
  locked?: boolean;
  /** Held rather than tapped: the reactions, over the card. */
  onHold?: (at: DOMRect) => void;
  onOpen: () => void;
}) {
  const late = post.data.lateMinutes ?? 0;
  const node = useSeenWhenShown(post, uid, !locked);
  // Not on a frosted card, which has nothing yet to react to.
  const hold = useHold((at) => onHold?.(at));

  return (
    <button
      ref={node}
      className={fresh ? 'opened-card is-new' : 'opened-card'}
      data-post={post.id}
      data-locked={locked || undefined}
      aria-label={locked ? `${author?.displayName ?? 'Someone'} posted. Post yours to see it.` : undefined}
      {...(locked || !onHold ? {} : hold.handlers)}
      onClick={() => {
        // The press that brought the reactions up is not also a tap.
        if (hold.consumed()) return;
        onOpen();
      }}
    >
      <span className="opened-shot">
        {/*
          Not the photograph blurred: that would mean fetching it, and anyone
          could unblur it. A frost of its own instead, and the photo is only
          downloaded once there is something to show.
        */}
        {locked ? (
          <span className="locked-shot">
            <span className="locked-call">Post yours to see</span>
          </span>
        ) : (
          <Shot post={post} height="100%" />
        )}
        {/*
          Where the only free corner is. The other three already carry the
          selfie, the on-time mark and the name. After the photograph, as they
          are, so nothing drawn for it can land on top. It stays for the whole
          visit, like the line under it, rather than going out the moment the
          card is read.
        */}
        {/*
          One row along the bottom: whose it is on the left, and on the right
          what the family made of it, then New. The reactions and comments
          used to sit in a strip under the card and went with it; Carter
          wanted them back, on the picture this time.
        */}
        <span className="card-foot">
          <span className="name-pill">
            <Avatar name={author?.displayName ?? '?'} photoURL={avatarOf(author)} size={20} />
            <span className="name-pill-name">{author?.displayName ?? 'Someone'}</span>
            <StreakMark days={authorStreak} />
          </span>
          {/* Not on a frosted card: what people made of it is part of seeing it. */}
          {!locked && <CardActivity post={post} uid={uid} />}
          {fresh && <span className="new-pill">New</span>}
        </span>
        {/*
          How the prompt was answered, which is the only thing left that marks
          having been there for it -- the streak counts the day as readily as
          the prompt now.

          Both halves, because only saying the good one made its absence the
          message: a photograph with no badge could be late or could be from
          before any of this was recorded, and there was no telling which.
        */}
        <span className={late > 0 ? 'on-time on-time--late on-time--corner' : 'on-time on-time--corner'}>
          {late > 0 ? `${lateText(late)} late` : 'on time'}
        </span>
      </span>

    </button>
  );
}

/**
 * The top few reactions and a total, and how many comments: nothing if neither.
 * On your own, comments you have not opened yet show as how many are new.
 */
export function CardActivity({ post, uid }: { post: DayPost; uid: string }) {
  const seen = useSeenTalk();
  const tally = tallyEmoji(post.data.reactions);
  const reactions = tally.reduce((sum, each) => sum + each.count, 0);
  const comments = post.data.replyCount ?? 0;
  if (reactions === 0 && comments === 0) return null;
  const fresh = waitingOnMe(post, uid, seen) ? unreadOf(post, seen) : 0;

  const said = [
    reactions > 0 && `${reactions} ${reactions === 1 ? 'reaction' : 'reactions'}`,
    fresh > 0
      ? `${fresh} new ${fresh === 1 ? 'comment' : 'comments'}`
      : comments > 0 && `${comments} ${comments === 1 ? 'comment' : 'comments'}`,
  ].filter(Boolean);

  return (
    <span className="card-activity" role="img" aria-label={said.join(', ')}>
      {reactions > 0 && (
        <span className="card-activity-part">
          <span className="card-activity-emoji">{tally.slice(0, 3).map((each) => each.emoji).join('')}</span>
          {reactions}
        </span>
      )}
      {comments > 0 && (
        <span className={fresh > 0 ? 'card-activity-part is-unread' : 'card-activity-part'}>
          <IconChat size={16} />
          {fresh > 0 ? `${fresh} new` : comments}
        </span>
      )}
    </span>
  );
}

function Shot({ post, height }: { post: WithId<DailyPostDoc>; height: number | string }) {
  const data = post.data;

  if (data.video) {
    return (
      <video
        src={urlFor(data.video)}
        muted
        playsInline
        preload="metadata"
        style={{
          width: '100%',
          height,
          objectFit: 'cover',
          background: 'var(--well)',
          display: 'block',
        }}
      />
    );
  }

  return (
    <>
      <Media media={data.back} height={height} radius={0} />
      {data.front && (
        <span className="inset-shot">
          <Media media={data.front} height="100%" radius={0} />
        </span>
      )}
      {data.status === 'processing' && (
        <span className="center" style={{ position: 'absolute', inset: 0, background: 'rgba(11,13,18,0.5)' }}>
          <Spinner />
        </span>
      )}
    </>
  );
}

function WindowCard({
  today,
  hasPosted,
  onOpenCamera,
}: {
  today: TodayState;
  hasPosted: boolean;
  onOpenCamera: () => void;
}) {
  const firedAt = today.daily?.firedAt;

  if (!firedAt) {
    return (
      <div className="card row" style={{ padding: 16, gap: 14 }}>
        <span className="grow col" style={{ gap: 4 }}>
          <span className="bold" style={{ fontSize: 15 }}>
            Not yet today
          </span>
          <span className="muted small">It fires at a random time. You will get a buzz.</span>
        </span>
        {/* Not "in early". Before today's prompt fires the live one is still
            yesterday's, so a GiviSnap taken this morning answered that — which
            is the same day the streak counts it for. */}
        {hasPosted && <Chip tone="saved">yesterday's in</Chip>}
      </div>
    );
  }

  return (
    <div
      className="row"
      style={{
        padding: 16,
        gap: 14,
        borderRadius: 'var(--r-lg)',
        background: 'var(--accent-tint)',
        border: '1px solid var(--line)',
      }}
    >
      <span className="grow col" style={{ gap: 4 }}>
        <span className="bold" style={{ fontSize: 15 }}>
          You have not posted
        </span>
        <span className="muted small">Your streak holds until the next one fires.</span>
      </span>
      <button className="btn" onClick={onOpenCamera} style={{ minHeight: 40, padding: '0 18px' }}>
        Post
      </button>
    </div>
  );
}

function Media({
  media,
  height,
  radius,
}: {
  media: MediaRef | null;
  height: number | string;
  radius: number;
}) {
  if (!media) return <MediaWell height={height} radius={radius} />;
  return (
    <Photo
      src={urlFor(shownMedia(media))}
      preview={media.blur}
      alt=""
      loading="lazy"
      style={{
        width: '100%',
        height,
        objectFit: 'cover',
        borderRadius: radius,
        backgroundColor: 'var(--well)',
        display: 'block',
      }}
    />
  );
}
